import React from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Breadcrumbs, Button, Link, Paper, Typography } from '@mui/material';
import { getQuestionnaireSummary } from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { CoreQuestionnairesTab } from '@/components/questionnaires/final-page/CoreQuestionnairesTab';
import { CustomQuestionnairesTab } from '@/components/questionnaires/final-page/CustomQuestionnairesTab';
import { QuestionnaireSummaryCard } from '@/components/questionnaires/final-page/QuestionnaireSummaryCard';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import type {
  QuestionnaireSummaryCoreResponse,
  QuestionnaireSummaryCustomResponse,
} from '@/lib/api/questionnaires';

type QuestionnaireTab = 'core' | 'custom';
type ViewMode = 'grid' | 'list';
type CoreFilter = 'all' | 'assigned' | 'conditional';
type CoreGroup = 'all' | 'diabetes' | 'thyroid' | 'insulin-resistance' | 'pcos';
type CustomFilter = 'all' | 'assigned' | 'draft';

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '32px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  textTransform: 'capitalize',
  color: '#1e293b',
} as const;

const pageSubtitleSx = {
  fontFamily: 'Arial, sans-serif',
  fontWeight: 400,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: { xs: '20px', sm: '20px' },
  letterSpacing: 0,
  textAlign: 'left',
  color: '#667085',
} as const;

const questionnairesActionTourSteps: ActionTourStep[] = [
  {
    id: 'questionnaires-tabs',
    title: 'Core and Custom Questionnaire',
    description: 'Use Core Questionnaire for system templates and Custom Questionnaire for drafts or published versions you have customized.',
  },
  {
    id: 'questionnaires-add-custom',
    title: 'Add Custom Questions',
    description: 'The plus action opens the questionnaire customization flow. Use it when you want to create or edit a custom version before assigning it.',
  },
  {
    id: 'questionnaires-preview',
    title: 'Preview Button',
    description: 'Preview opens the questionnaire detail page so you can view the questionnaire before sharing it with clients.',
  },
  {
    id: 'questionnaires-responses',
    title: 'Responses Button',
    description: 'Responses opens the response page. From there you can see client statuses, copy the share link, fill manually, send reminders, and view completed answers.',
  },
  {
    id: 'questionnaires-assign',
    title: 'Assign Button',
    description: 'Assign opens the assignment page where you select clients, set optional deadlines and messages, choose email delivery, and send the questionnaire.',
  },
];

function parseTab(value: string | null): QuestionnaireTab {
  return value === 'custom' ? 'custom' : 'core';
}

function parseViewMode(value: string | null): ViewMode {
  return value === 'list' ? 'list' : 'grid';
}

function parseCoreFilter(value: string | null): CoreFilter {
  if (value === 'assigned' || value === 'conditional') return value;
  return 'all';
}

function parseCoreGroup(value: string | null): CoreGroup {
  if (value === 'diabetes' || value === 'thyroid' || value === 'insulin-resistance' || value === 'pcos') {
    return value;
  }
  return 'all';
}

function parseCustomFilter(value: string | null): CustomFilter {
  if (value === 'assigned' || value === 'draft') return value;
  return 'all';
}

const QuestionnairesFinalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getCurrentUser();
  const nutritionistId = user?.id;

  const activeTab = parseTab(searchParams.get('tab'));

  const coreSummaryQuery = useQuery({
    queryKey: ['questionnaireSummary', 'core', nutritionistId],
    queryFn: () => getQuestionnaireSummary('core', nutritionistId),
    enabled: Boolean(nutritionistId) && activeTab === 'core',
  });

  const customSummaryQuery = useQuery({
    queryKey: ['questionnaireSummary', 'custom', nutritionistId],
    queryFn: () => getQuestionnaireSummary('custom', nutritionistId),
    enabled: Boolean(nutritionistId) && activeTab === 'custom',
  });

  const coreSummary =
    coreSummaryQuery.data?.type === 'core'
      ? (coreSummaryQuery.data as QuestionnaireSummaryCoreResponse)
      : undefined;
  const customSummary =
    customSummaryQuery.data?.type === 'custom'
      ? (customSummaryQuery.data as QuestionnaireSummaryCustomResponse)
      : undefined;
  const activeSummary = React.useMemo(() => {
    const summary = activeTab === 'core' ? coreSummary : customSummary;
    return {
      totalQuestionnaires: summary?.totalQuestionnaires ?? 0,
      activeClients: summary?.activeClientsCount ?? 0,
      responsesThisWeek: summary?.responsesThisWeek ?? 0,
      pendingResponses: summary?.pendingResponsesCount ?? 0,
    };
  }, [activeTab, coreSummary, customSummary]);

  const viewMode = parseViewMode(searchParams.get('view'));
  const coreFilter = parseCoreFilter(searchParams.get('coreFilter'));
  const coreGroup = parseCoreGroup(searchParams.get('coreGroup'));
  const customFilter = parseCustomFilter(searchParams.get('customFilter'));

  const updateSearchParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value == null || value.trim() === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const handleTabChange = React.useCallback(
    (tab: QuestionnaireTab) => {
      updateSearchParams({ tab });
    },
    [updateSearchParams]
  );

  const handleViewModeChange = React.useCallback(
    (nextViewMode: ViewMode) => {
      updateSearchParams({ view: nextViewMode });
    },
    [updateSearchParams]
  );

  const handleCoreFilterChange = React.useCallback(
    (filter: CoreFilter) => {
      updateSearchParams({
        coreFilter: filter,
        coreGroup: filter === 'conditional' ? coreGroup : 'all',
      });
    },
    [coreGroup, updateSearchParams]
  );

  const handleCoreGroupChange = React.useCallback(
    (group: CoreGroup) => {
      updateSearchParams({ coreGroup: group, coreFilter: 'conditional' });
    },
    [updateSearchParams]
  );

  const handleCustomFilterChange = React.useCallback(
    (filter: CustomFilter) => {
      updateSearchParams({ customFilter: filter });
    },
    [updateSearchParams]
  );

  return (
    <Box data-demo-id="questionnaires-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f8fafc',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Questionnaires
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
              <Typography sx={pageTitleSx}>Questionnaires</Typography>
              <Typography sx={{ ...pageSubtitleSx, width: 'fit-content', maxWidth: '100%', overflowWrap: 'anywhere' }}>
                Manage and assign questionnaires to your clients
              </Typography>
            </Box>
            <NotificationCenter buttonVariant="feature" />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '17px',
            }}
          >
            <QuestionnaireSummaryCard title="Total Questionnaires" value={activeSummary.totalQuestionnaires} icon="notebook" />
            <QuestionnaireSummaryCard title="Active Clients" value={activeSummary.activeClients} icon="usersGroup" />
            <QuestionnaireSummaryCard title="Responses this Week" value={activeSummary.responsesThisWeek} icon="responses" />
            <QuestionnaireSummaryCard title="Pending Responses" value={activeSummary.pendingResponses} icon="pending" />
          </Box>

          <Box data-action-tour-id="questionnaires-tabs" sx={{ mt: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 0,
                p: '6.19px',
                borderRadius: '7.43px',
                bgcolor: '#E8EBF0',
                alignItems: 'stretch',
                width: { xs: '100%', sm: 'fit-content' },
                maxWidth: '100%',
              }}
            >
              <Button
                data-demo-id="questionnaires-core-tab"
                variant="text"
                onClick={() => handleTabChange('core')}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: '100%', sm: 160 },
                  minHeight: 44,
                  borderRadius: { xs: '7.43px', sm: '7.43px 0 0 7.43px' },
                  padding: '6.19px 16px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: activeTab === 'core' ? 600 : 400,
                  fontSize: '14px',
                  color: activeTab === 'core' ? '#1A202C' : '#4A5568',
                  bgcolor: activeTab === 'core' ? '#FFFFFF' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeTab === 'core' ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                Core Questionnaire
              </Button>
              <Button
                data-demo-id="questionnaires-custom-tab"
                variant="text"
                onClick={() => handleTabChange('custom')}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: '100%', sm: 160 },
                  minHeight: 44,
                  borderRadius: { xs: '7.43px', sm: '0 7.43px 7.43px 0' },
                  padding: '6.19px 16px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: activeTab === 'custom' ? 600 : 400,
                  fontSize: '14px',
                  color: activeTab === 'custom' ? '#1A202C' : '#4A5568',
                  bgcolor: activeTab === 'custom' ? '#FFFFFF' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeTab === 'custom' ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                Custom Questionnaire
              </Button>
            </Box>
          </Box>

          {activeTab === 'core' ? (
            <CoreQuestionnairesTab
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              workflowFilter={coreFilter}
              onWorkflowFilterChange={handleCoreFilterChange}
              selectedConditionalGroup={coreGroup}
              onConditionalGroupChange={handleCoreGroupChange}
              summary={coreSummary}
            />
          ) : (
            <CustomQuestionnairesTab
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              workflowFilter={customFilter}
              onWorkflowFilterChange={handleCustomFilterChange}
              summary={customSummary}
            />
          )}
        </Box>
      </Paper>
      <ActionTour
        ariaLabel="Questionnaires action guide"
        label="Questionnaire guide"
        storageKey="caloriescience:questionnaires-action-guide:v1"
        steps={questionnairesActionTourSteps}
      />
    </Box>
  );
};

export default QuestionnairesFinalPage;
